import type { TipInstance, TooltipOptions } from '$lib/actions/tooltip/types';
import { createClassComponent } from 'svelte/legacy';
import Tooltip from './Tooltip.svelte';

const clickEvent
	= typeof window !== 'undefined' && 'ontouchstart' in window ? 'touchstart' : 'click';

const defaultOptions: Partial<TooltipOptions> = {
	on: 'hover',
	delay: 500,
	placement: 'top',
};

const tips: Map<HTMLElement, TipInstance> = new Map();
let activeNode: HTMLElement | null;
let activeDelayTimer: ReturnType<typeof setTimeout> | null = null;

function configureTooltip(node: HTMLElement, options: TooltipOptions | undefined) {
	if (!options) {
		removeTooltip(node);
		return;
	}
	const config = tips.get(node);
	const newOptions: TooltipOptions = {
		...defaultOptions,
		...options,
	};

	if (!config) {
		tips.set(node, { options: newOptions });
	} else {
		config.options = newOptions;
		config.component?.$set({ options: newOptions });
	}
	updateListeners();
}

function removeTooltip(node: HTMLElement) {
	if (node === activeNode) {
		hideTooltip();
	}
	tips.delete(node);
	updateListeners();
}

let listening = false;
function updateListeners() {
	if (!document) {
		return;
	}
	const shouldListen = tips.size > 0;
	let action: 'addEventListener' | 'removeEventListener' | undefined;
	if (listening && !shouldListen) {
		action = 'removeEventListener';
	} else if (!listening && shouldListen) {
		action = 'addEventListener';
	}
	if (action) {
		document[action]('mouseenter', mouseenter, true);
		document[action]('scroll', scroll, true);
		document[action](clickEvent, click, true);
		listening = action === 'addEventListener';
	}
}

function mouseenter(e: Event) {
	if (tips.get(e.target as HTMLElement)?.options.on !== 'click') {
		showTooltip(e.target as HTMLElement);
	}
}

function scroll() {
	hideTooltip();
}

function click(e: Event) {
	if (activeNode && tips.get(activeNode)?.options.on === 'click') {
		hideTooltip();
	} else if (tips.get(e.target as HTMLElement)?.options.on === 'click') {
		showTooltip(e.target as HTMLElement);
	}
}

function showTooltip(node: HTMLElement) {
	if (node === activeNode) {
		return;
	}
	const tip = tips.get(node);
	if (!tip || tip.component) {
		return;
	}
	if (tip.options.on !== 'click') {
		const hideListener = () => {
			node.removeEventListener('mouseleave', hideListener, true);
			hideTooltip();
		};
		node.addEventListener('mouseleave', hideListener, true);
	}
	if (tip.options.delay && tip.options.on !== 'click') {
		activeDelayTimer = setTimeout(() => {
			renderTip(node, tip);
		}, tip.options.delay);
	} else {
		renderTip(node, tip);
	}

	activeNode = node;
}

function renderTip(node: HTMLElement, tip: TipInstance) {
	tip.component = createClassComponent({
		component: Tooltip,
		target: document.body,
		props: { options: tip.options, trigger: node },
	});
}

function hideTooltip() {
	if (activeDelayTimer) {
		clearTimeout(activeDelayTimer);
		activeDelayTimer = null;
	}
	if (activeNode) {
		const config = tips.get(activeNode);
		const component = config?.component;
		if (component) {
			config.component = undefined;
			component.$on('hidden', () => {
				component.$destroy();
			});
			component.__hide();
		}
		activeNode = null;
	}
}

export function tooltip(node: HTMLElement, options: TooltipOptions | undefined) {
	configureTooltip(node, options);

	return {
		update(options: TooltipOptions) {
			configureTooltip(node, options);
		},
		destroy() {
			removeTooltip(node);
		},
	};
}
